/* Welcome popup, engagement-aware edition.
   Opens on scroll engagement (25%), exit intent, or a generous timeout,
   whichever happens first. Never on cart or builder pages. Submits the
   signup in the background (no reload) with a native-submit fallback if
   Shopify responds with a bot challenge. Shows once per visitor. */
class CandyPopup extends HTMLElement {
  connectedCallback() {
    this.STORAGE_KEY = 'lak-welcome-popup';
    this.dialog = this.querySelector('[data-popup-dialog]');
    this.overlay = this.querySelector('[data-popup-overlay]');
    this.closeBtn = this.querySelector('[data-popup-close]');
    this.noThanks = this.querySelector('[data-popup-nothanks]');
    this.form = this.querySelector('form');
    this.formArea = this.querySelector('[data-popup-form-area]');
    this.successArea = this.querySelector('[data-popup-success]');
    this.errorEl = this.querySelector('[data-popup-error]');
    this.input = this.querySelector('input[type="email"]');
    this.submitBtn = this.querySelector('[data-popup-submit]');

    const path = window.location.pathname;
    const suppressed = path.includes('/cart') || path.includes('/pages/build-your-own');
    const done = window.localStorage.getItem(this.STORAGE_KEY);
    const openNow = this.dataset.openNow === 'true';

    if (this.dataset.success === 'true') {
      window.localStorage.setItem(this.STORAGE_KEY, 'subscribed');
    }

    if (openNow) {
      this.open(false);
    } else if (!done && !suppressed) {
      this.armTriggers();
    }

    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close(true));
    if (this.noThanks) this.noThanks.addEventListener('click', () => this.close(true));
    if (this.overlay) this.overlay.addEventListener('click', () => this.close(true));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close(true);
    });
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  armTriggers() {
    this.triggered = false;
    const fire = () => {
      if (this.triggered) return;
      this.triggered = true;
      this.disarmTriggers();
      this.open(false);
    };

    // 1. scroll engagement: 25% of the page
    this.onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      if (scrolled > document.documentElement.scrollHeight * 0.25 && window.scrollY > 200) fire();
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    // 2. exit intent (desktop): cursor leaves through the top edge
    this.onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) fire();
    };
    document.addEventListener('mouseout', this.onMouseOut);

    // 3. generous fallback timer
    this.timer = setTimeout(fire, 15000);
  }

  disarmTriggers() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.onMouseOut) document.removeEventListener('mouseout', this.onMouseOut);
    if (this.timer) clearTimeout(this.timer);
  }

  disconnectedCallback() {
    this.disarmTriggers();
    if (this.autoCloseTimer) clearTimeout(this.autoCloseTimer);
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = this.submitBtn.dataset.loadingText || 'Sending...';
    }
    if (this.errorEl) this.errorEl.hidden = true;

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { 'Accept': 'text/html' }
      });

      if (response.url && response.url.includes('challenge')) {
        // bot challenge: let the browser do a full submit so the user can pass it
        this.form.submit();
        return;
      }

      if (response.ok) {
        this.showSuccess();
      } else {
        this.showError();
      }
    } catch (e) {
      this.showError();
    }
  }

  showSuccess() {
    window.localStorage.setItem(this.STORAGE_KEY, 'subscribed');
    if (this.formArea) this.formArea.hidden = true;
    if (this.successArea) {
      this.successArea.hidden = false;
      this.successArea.focus({ preventScroll: true });
    }
    this.autoCloseTimer = setTimeout(() => this.close(false), 6000);
  }

  showError() {
    if (this.submitBtn) {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent = this.submitBtn.dataset.label || 'Send my code';
    }
    if (this.errorEl) this.errorEl.hidden = false;
  }

  open(focusInput) {
    this.removeAttribute('hidden');
    document.body.classList.add('candy-popup-open');
    if (focusInput && this.input) {
      this.input.focus({ preventScroll: true });
    } else if (this.closeBtn) {
      this.closeBtn.focus({ preventScroll: true });
    }
  }

  close(remember) {
    this.setAttribute('hidden', '');
    document.body.classList.remove('candy-popup-open');
    this.disarmTriggers();
    if (remember) window.localStorage.setItem(this.STORAGE_KEY, 'dismissed');
  }
}

customElements.define('candy-popup', CandyPopup);
